import { useCallback, useEffect, useMemo, useState } from "react"

import api, { getEnterpriseId } from "../../../lib/api"
import { emitSnackbar } from "../../../lib/snackbar-emitter"
import { loader } from "../../../lib/loader"
import manageErrors from "../../../lib/error-management"

const CATEGORIES_PATH = (enterpriseId) => `enterprises/${enterpriseId}/category/`

const normalizeCollection = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  if (Array.isArray(data?.data)) return data.data
  return []
}

const parseApiItem = (item = {}) => ({
  id: item.id,
  name: item.name || "",
  description: item.description || "",
})

const toApiPayload = ({ name, description }) => ({
  name: name?.trim(),
  description: description?.trim() || "",
})

export const useCategories = () => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null)

  const enterpriseId = getEnterpriseId()

  const load = useCallback(async () => {
    if (!enterpriseId) return

    setLoading(true)
    loader.on()

    try {
      const response = await api.get(CATEGORIES_PATH(enterpriseId))
      if (response.status === 200) {
        const list = normalizeCollection(response.data)
        setItems(list.map(parseApiItem))
      } else {
        emitSnackbar({ message: manageErrors(response.data), severity: "error" })
      }
    } catch (error) {
      emitSnackbar({ message: "Erro ao carregar as categorias.", severity: "error" })
    } finally {
      loader.off()
      setLoading(false)
    }
  }, [enterpriseId])

  useEffect(() => {
    void load()
  }, [load])

  const save = useCallback(
    async (payload) => {
      const isEdit = Boolean(payload?.id)

      try {
        loader.on()

        const response = isEdit
          ? await api.patch(`${CATEGORIES_PATH(enterpriseId)}${payload.id}/`, toApiPayload(payload))
          : await api.post(CATEGORIES_PATH(enterpriseId), toApiPayload(payload))

        if (response.status === 200 || response.status === 201) {
          emitSnackbar({
            message: isEdit
              ? "Categoria atualizada com sucesso."
              : "Categoria criada com sucesso.",
            severity: "success",
          })
          await load()
          return true
        }

        emitSnackbar({ message: manageErrors(response.data), severity: "error" })
        return false
      } catch (error) {
        const serverError = error?.response?.data || (error?.message && String(error.message)) || ''
        emitSnackbar({
          message: `Erro ao salvar a categoria.${serverError ? ` (${serverError})` : ""}`,
          severity: "error",
        })
        return false
      } finally {
        loader.off()
      }
    },
    [enterpriseId, load],
  )

  const remove = useCallback(
    async (item) => {
      if (!item?.id) return false

      try {
        loader.on()
        const response = await api.delete(`${CATEGORIES_PATH(enterpriseId)}${item.id}/`)
        if (response.status === 204) {
          emitSnackbar({ message: "Categoria excluída com sucesso.", severity: "success" })
          await load()
          return true
        }

        emitSnackbar({ message: manageErrors(response.data), severity: "error" })
        return false
      } catch (error) {
        emitSnackbar({ message: "Erro ao excluir a categoria.", severity: "error" })
        return false
      } finally {
        loader.off()
      }
    },
    [enterpriseId, load],
  )

  const actions = useMemo(
    () => ({
      setEditing,
      setDeleting,
      setItems,
      setLoading,
    }),
    [],
  )

  return {
    items,
    loading,
    editing,
    deleting,
    setEditing,
    setDeleting,
    load,
    save,
    remove,
    actions,
  }
}
